/* eslint-disable multiline-ternary */
import isEmpty from 'lodash/isEmpty';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { KEY_ADS_POSITION } from 'src/components/ads/dynamicAds';
import SectionColumnist from 'src/components/sections/section-events/section-columnist';
import SectionLatestStories from 'src/components/sections/section-events/section-latest-stories';
import WidgetHeader from 'src/components/widgets/widget-header';
import { API_URI } from 'src/constants';
import { IAds } from 'src/modules/ads';
import { Data } from 'src/modules/sections/section-1';
import { GET } from 'src/services';
import { DATALAYER_TYPE } from 'src/utils/datalayer/send-datalayer';
import { convertDatasets, groupKeyValueToObjectList } from 'src/utils/datasets';
import { buildPlaylistIds, mapYoutubePlaylistsToCardMostView } from 'src/utils/helper';
import { GET as GET_UTIL } from 'src/utils/service';
import styled from 'styled-components';

import SkeletonColumnist from 'src/components/skeletons/skeleton-columnist';
import SkeletonHighlight from 'src/components/skeletons/skeleton-highlight';

const CardV = dynamic(() => import('src/components/cards/card-v'));
const Carousels = dynamic(() => import('src/components/carousels'));
const DynamicAds = dynamic(() => import('src/components/ads/dynamicAds'), { ssr: false });
const SectionHighlight = dynamic(() => import('src/components/sections/section-highlight'), { ssr: true });

const SectionFeaturedGrid = dynamic(() => import('src/components/sections/section-events/section-featured-grid'), { ssr: true });

const SectionShow = dynamic(() => import('src/components/sections/section-events/section-show'));
const CardVideoList = dynamic(() => import('src/components/cards/card-video-list'));
const SectionShort = dynamic(() => import('src/components/sections/section-events/section-short'));
const SectionPartnership = dynamic(() => import('src/components/sections/section-events/section-partner-ship'));

type Datas = {
  data: Data;
  highlightText?: any;
  subMenu: any;
  navBar: any;
  ads: IAds[];
  sectionMember: any;
};

const RESERVED = {
  highlight: { desktop: 1020, mobile: 560 },
  columnist: { desktop: 420, mobile: 520 },
  latestStories: { desktop: 640, mobile: 860 },
  show: { desktop: 520, mobile: 640 },
  videoList: { desktop: 380, mobile: 480 },
  short: { desktop: 460, mobile: 560 },
  partnership: { desktop: 320, mobile: 400 },
  adInarticle: { desktop: 280, mobile: 300 }
} as const;

function useLazySection<T extends HTMLElement = HTMLDivElement>(rootMargin = '400px') {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isVisible) return;
    const el = ref.current;
    if (!el) return;

    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        });
      },
      { rootMargin, threshold: 0 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [isVisible, rootMargin]);

  return [ref, isVisible] as const;
}

function useContentPainted<T extends HTMLElement = HTMLDivElement>(minHeight = 100) {
  const ref = useRef<T | null>(null);
  const [painted, setPainted] = useState(false);

  useEffect(() => {
    if (painted) return;
    const el = ref.current;
    if (!el) return;

    const check = () => {
      if (el.getBoundingClientRect().height >= minHeight) {
        setPainted(true);
        return true;
      }
      return false;
    };

    if (check()) return;

    // fallback: browser ที่ไม่มี ResizeObserver → แสดง content เลย
    if (typeof ResizeObserver === 'undefined') {
      setPainted(true);
      return;
    }

    const ro = new ResizeObserver(() => {
      if (check()) ro.disconnect();
    });
    ro.observe(el);

    // safety net: ถ้าเกิน 5 วิ ยังไม่ paint ก็ปิด skeleton ทิ้ง
    // (ดีกว่าปล่อยให้ shimmer ค้างตลอดกาลถ้า API พัง)
    const timeout = setTimeout(() => setPainted(true), 5000);

    return () => {
      ro.disconnect();
      clearTimeout(timeout);
    };
  }, [painted, minHeight]);

  return [ref, painted] as const;
}

const SectionOne = ({ data, highlightText, subMenu, navBar, ads }: Datas) => {
  const router = useRouter();

  const [latestStoriesRef, latestStoriesVisible] = useLazySection<HTMLDivElement>('500px');
  const [showRef, showVisible] = useLazySection<HTMLDivElement>('500px');
  const [shortRef, shortVisible] = useLazySection<HTMLDivElement>('400px');
  const [partnershipRef, partnershipVisible] = useLazySection<HTMLDivElement>('400px');

  /* ----------------------------- States data ------------------------------ */
  const [dataPopular, setDataPopular] = useState<any[]>([]);
  const [dataSet, setDataSet] = useState<any[] | null>(null); // เก็บผลลัพธ์ของ /api/datasets มาใช้ซ้ำ
  const [dataSetsShowList, setDataSetsShowList] = useState<any[]>([]);
  const [dataSetsShow, setDataSetsShow] = useState<any[]>([]);
  const [dataSetsShort, setDataSetsShort] = useState<any[]>([]);
  const [dataSetsPartnership, setDataSetsPartnership] = useState<any[]>([]);
  const [sectionDataVideoList, setSectionDataVideoList] = useState<any[]>([]);

  const [highlightContentRef, highlightPainted] = useContentPainted<HTMLDivElement>(300);
  const [columnistContentRef, columnistPainted] = useContentPainted<HTMLDivElement>(200);
  const inflightDataSet = useRef<Promise<any[]> | null>(null);

  const ensureDataSet = useCallback(async (): Promise<any[]> => {
    if (dataSet !== null) return dataSet;
    if (inflightDataSet.current) return inflightDataSet.current;

    inflightDataSet.current = (async () => {
      try {
        const res = await GET_UTIL('/api/datasets');
        const list = Array.isArray(res) ? res : [];
        setDataSet(list);
        return list;
      } catch (error) {
        console.error('ensureDataSet -> error', error);
        setDataSet([]);
        return [];
      } finally {
        inflightDataSet.current = null;
      }
    })();

    return inflightDataSet.current;
  }, [dataSet]);

  useEffect(() => {
    if (!latestStoriesVisible || !isEmpty(dataPopular)) return;
    (async () => {
      try {
        const res = await GET('https://api.thepeople.co/api/v1.0/categories/popular-news', true, false);
        if (!isEmpty(res)) setDataPopular(res);
      } catch (error) {
        console.error('fetch popular-news -> error', error);
      }
    })();
  }, [latestStoriesVisible]);

  useEffect(() => {
    if (!showVisible) return;
    (async () => {
      const ds = await ensureDataSet();
      if (isEmpty(ds)) return;

      const showList = convertDatasets(ds, 'section-youtube-list', false) || [];
      const show = convertDatasets(ds, 'lock-data-show', false) || [];
      setDataSetsShowList(showList);
      setDataSetsShow(show);

      // fetch youtube playlists (ต้องรอ showList ก่อน)
      try {
        const groupedLists = groupKeyValueToObjectList(showList[0])?.[0];
        const listsId = buildPlaylistIds(groupedLists);
        if (!listsId) return;
        const uri = `${API_URI}/api/youtube/playlists?part=snippet,contentDetails&id=${listsId}`;
        const res = await GET(uri, true, false);
        const items = res?.items || [];
        setSectionDataVideoList(mapYoutubePlaylistsToCardMostView(items, groupedLists));
      } catch (error) {
        console.error('fetch youtube playlists -> error', error);
      }
    })();
  }, [showVisible, ensureDataSet]);

  useEffect(() => {
    if (!shortVisible) return;
    (async () => {
      const ds = await ensureDataSet();
      if (isEmpty(ds)) return;
      setDataSetsShort(convertDatasets(ds, 'section-short', false) || []);
    })();
  }, [shortVisible, ensureDataSet]);

  useEffect(() => {
    if (!partnershipVisible) return;
    (async () => {
      const ds = await ensureDataSet();
      if (isEmpty(ds)) return;
      setDataSetsPartnership(convertDatasets(ds, 'section-partnership', false) || []);
    })();
  }, [partnershipVisible, ensureDataSet]);

  if (isEmpty(data)) return null;

  const newsI = data?.block1?.data;
  const LatestNews = data?.headerLatestNews;
  const newsII = data?.block2?.data;
  const newsIII = data?.block3?.data;
  const titleNewsIII = data?.block3;
  const lockIII = data?.newHighlight3;
  const _dataIII = lockIII && newsI && [...lockIII, ...newsI];
  const resultDateIII = _dataIII && lockIII ? _dataIII.filter(el => lockIII.every(val => val.id !== el.id)) : newsI;

  const columnistData = navBar.filter((item: any) => item.nameEng === 'columnist');

  const latestStoriesReady = latestStoriesVisible && !isEmpty(resultDateIII) && !isEmpty(dataPopular);
  const showReady = showVisible && !isEmpty(dataSetsShow);
  const videoListReady = showVisible && !isEmpty(sectionDataVideoList);
  const shortReady = shortVisible && !isEmpty(dataSetsShort[0]);
  const partnershipReady = partnershipVisible && !isEmpty(dataSetsPartnership);

  return (
    <SectionWrapper>
      <LazySlot className='container' $h={RESERVED.highlight.desktop} $hMobile={RESERVED.highlight.mobile}>
        {!highlightPainted && <SkeletonHighlight />}
        <div ref={highlightContentRef} className='sk-content' data-painted={highlightPainted}>
          <SectionHighlight unsetBottom={true} ads={ads} highlightText={highlightText} subMenu={subMenu} navBar={navBar} />
        </div>
      </LazySlot>

      <LazySlot $h={RESERVED.columnist.desktop} $hMobile={RESERVED.columnist.mobile}>
        {!columnistPainted && <SkeletonColumnist />}
        <div ref={columnistContentRef} className='sk-content' data-painted={columnistPainted}>
          {!isEmpty(columnistData) && <SectionColumnist data={columnistData[0]} defaultData={resultDateIII} title={columnistData[0]?.nameTh} link={columnistData[0]?.link} showHeader={true} />}
        </div>
      </LazySlot>

      <LazySlot ref={latestStoriesRef} $h={RESERVED.latestStories.desktop} $hMobile={RESERVED.latestStories.mobile} $released={latestStoriesReady}>
        {latestStoriesReady && <SectionLatestStories latestNews={LatestNews} mostView={dataPopular} title='LATEST STORIES' mostViewTitle='MOST VIEW' />}
      </LazySlot>

      {!isEmpty(newsII) && <SectionFeaturedGrid data={newsII} title={data?.block2?.title} link={data?.block2?.link} />}

      <LazySlot ref={showRef} $h={RESERVED.show.desktop} $hMobile={RESERVED.show.mobile} $released={showReady}>
        {showReady && <SectionShow data={dataSetsShow} title='SHOW' />}
      </LazySlot>

      <LazySlot $h={RESERVED.videoList.desktop} $hMobile={RESERVED.videoList.mobile} $released={videoListReady}>
        {videoListReady && (
          <div className='container'>
            {sectionDataVideoList.map((item, i) => (
              <CardVideoList
                key={i}
                data={item}
                showTitle={true}
                showBlurb={true}
                showArrow={true}
                external={true}
                target='_blank'
                eventDataLayer={{
                  type: DATALAYER_TYPE.TRACK_POSITION,
                  router: router?.pathname,
                  section: 'section-play-list',
                  position: `ใต้ : section-5`,
                  data: {
                    title: item?.title,
                    heading: `play-youtube`,
                    index: i + 1
                  }
                }}
              />
            ))}
          </div>
        )}
      </LazySlot>

      {!isEmpty(ads) && (
        <AdSlot className='container' $h={RESERVED.adInarticle.desktop} $hMobile={RESERVED.adInarticle.mobile}>
          <DynamicAds position={KEY_ADS_POSITION.INARTICLE_1} data={ads} />
        </AdSlot>
      )}

      <LazySlot ref={shortRef} $h={RESERVED.short.desktop} $hMobile={RESERVED.short.mobile} $released={shortReady}>
        {shortReady && <SectionShort data={dataSetsShort[0]} title='SHORT' />}
      </LazySlot>

      <LazySlot ref={partnershipRef} $h={RESERVED.partnership.desktop} $hMobile={RESERVED.partnership.mobile} $released={partnershipReady}>
        {partnershipReady && <SectionPartnership data={groupKeyValueToObjectList(dataSetsPartnership[0])[0]} title='Partnership' link='/shorts' />}
      </LazySlot>

      {!isEmpty(newsIII) && (
        <div className='block-three' id='section-5'>
          <WidgetHeader as='h2' title={titleNewsIII?.title} viewMoreLink={titleNewsIII?.link} titleColor='#ffffff' variant='line' position='left' showViewMore={false} />
          <div className='container br'>
            <Carousels infinite={true} slideShow={4} mobileSlideShowTwo={1} pauseOnHover={false} autoplaySpeed={6000} arrows={resultDateIII && true} dots={false}>
              {newsIII.map((item, i) => (
                <div key={i} className='card-items'>
                  <CardV
                    data={item}
                    showTitle={true}
                    showBlurb={false}
                    showByline={false}
                    type={'CARD_SMALL'}
                    showTime={false}
                    showCountview={false}
                    external={false}
                    eventDataLayer={{
                      type: DATALAYER_TYPE.TRACK_INDEX,
                      router: router?.pathname,
                      section: 'section-8',
                      position: `ใต้ : section-7`,
                      data: {
                        title: item?.title,
                        heading: data?.block3?.title,
                        carousel: i + 1
                      }
                    }}
                  />
                </div>
              ))}
            </Carousels>
          </div>
        </div>
      )}
    </SectionWrapper>
  );
};

const LazySlot = styled.div<{ $h: number; $hMobile?: number; $released?: boolean }>`
  ${({ $h, $hMobile, $released }) =>
    $released
      ? ''
      : `
    min-height: ${$h}px;
    @media (max-width: 768px) {
      min-height: ${$hMobile ?? $h}px;
    }
  `}
  content-visibility: auto;
  contain-intrinsic-size: auto ${({ $h }) => $h}px;
`;

const AdSlot = styled.div<{ $h: number; $hMobile?: number }>`
  min-height: ${({ $h }) => $h}px;
  display: flex;
  align-items: center;
  justify-content: center;
  @media (max-width: 768px) {
    min-height: ${({ $h, $hMobile }) => $hMobile ?? $h}px;
  }
`;

const SectionWrapper = styled.section`
  overflow-x: hidden;
  .card-items img,
  .card img {
    aspect-ratio: 16 / 9;
    width: 100%;
    height: auto;
  }

  .slick-dots {
    height: 20px;
  }

  .submenu {
    min-height: 121px;
    @media (max-width: 768px) {
      min-height: 85px;
    }
    @media (max-width: 475px) {
      min-height: 70px;
    }
    .submenu-list {
      list-style-type: none;
      display: flex;
      flex-wrap: wrap;
      padding: 0;
      margin: 0;
      width: 100%;
      text-align: center;
      li {
        width: 20%;
        max-width: 100%;
        @media (max-width: 480px) {
          width: unset;
          max-width: unset;
          padding: 0 10px;
        }
        p {
          width: fit-content;
          font-size: 24px;
          text-transform: uppercase;
          font-weight: 400;
          color: #b3b3b3;
          padding: 10px 0 2px 0;
          margin: auto;
          border-bottom: 2px solid transparent;
          @media (max-width: 768px) {
            font-size: 18px;
          }
          @media (max-width: 480px) {
            font-size: 12px;
          }
          &:hover {
            border-bottom: 2px solid #ed1a3b;
            color: #000 !important;
          }
          &.--active {
            border-bottom: 2px solid #ed1a3b;
            color: #000 !important;
          }
        }
      }
    }
    .border-bottom {
      margin-top: 30px;
      margin-right: 15px;
      margin-left: 15px;
      @media (max-width: 1024px) {
        margin-top: 10px;
        margin-right: unset;
        margin-left: unset;
      }
    }
    @media (max-width: 480px) {
      margin-top: -20px;
    }
  }

  .block-2 {
    padding-bottom: 30px;
    .slick-list {
      padding: 0 20px 20px 20px !important;
    }
    .slick-dots {
      bottom: 0;
    }

    .most-view-mobile {
      display: none;
    }

    @media (max-width: 480px) {
      .most-view-mobile {
        display: block;
        .side-right {
          overflow-y: scroll;
          height: 300px;
          &::-webkit-scrollbar {
            width: 4px;
            background-color: #f5f5f5;
          }

          &::-webkit-scrollbar-track {
            background-color: rgba(247, 247, 247, 0.9);
          }

          &::-webkit-scrollbar-thumb {
            border-radius: 50px;
            background-color: rgb(220 21 53);
          }
        }
      }
    }

    @media (max-width: 690px) {
      .title-left {
        margin-top: 30px;
      }
    }
  }
  .title-left {
    display: flex;
    align-items: center;
    justify-content: space-between;
    @media (max-width: 768px) {
      margin-top: 15px;
    }
    a {
      transition: all 0.4s ease-in-out;
      &:hover {
        color: #ed1a3b;
      }
    }
    h2 {
      text-transform: uppercase;
      font-size: 46px;
      font-weight: bold;
      @media (max-width: 768px) {
        font-size: 26px;
      }
    }
    p {
      padding: 2px 20px;
      background-color: rgb(220 21 53);
      color: #fff;
      border-radius: 8px;
      &:hover {
        color: #ed1a3b;
        background-color: rgb(255, 255, 255);
      }
    }
  }
  .title-right {
    padding-left: 6px;
    text-transform: capitalize;
    margin: 5px 0;
    @media (max-width: 768px) {
      margin: 20px 0 0 0;
    }
    h2 {
      font-size: 32px;
      font-weight: 400;
      @media (max-width: 768px) {
        font-size: 26px;
      }
      strong {
        color: #ed1a3b;
      }
    }
  }
  .card-items {
    padding-left: 10px;
    padding-right: 10px;
    @media (max-width: 768px) {
      padding-left: 0px;
      padding-right: 0px;
    }
  }
  .border-bottom {
    border-bottom: 1px solid #5f5f5f42;
  }
  .card-list {
    padding-bottom: 30px;
    font-weight: bold;
    .card {
      h3 {
        font-size: 16px;
      }
    }
  }

  /* block-one */
  .block-one {
    padding: 26px 0 0;
    @media (max-width: 768px) {
      .card-overlay {
        .card {
          h3 {
            width: 90%;
          }
        }
        .play-btn-video {
          bottom: 15px !important;
        }
      }
    }
    .slick-list {
      padding: 0 0 35px 0;
      @media (max-width: 768px) {
        padding: 0px;
      }
    }
    .slick-dots {
      bottom: 14px;
    }
    .most-view-desktop {
      display: block;
      .side-right {
        overflow-y: scroll;
        height: 300px;
        &::-webkit-scrollbar {
          width: 4px;
          background-color: #f5f5f5;
        }

        &::-webkit-scrollbar-track {
          background-color: rgba(247, 247, 247, 0.9);
        }

        &::-webkit-scrollbar-thumb {
          border-radius: 50px;
          background-color: rgb(220 21 53);
        }
      }
    }
    .title-left {
      @media (max-width: 768px) {
        margin-top: 0;
      }
    }
    @media (max-width: 480px) {
      flex-direction: column;
      .most-view-desktop {
        display: none;
      }
    }
  }

  /* block-two */
  .block-two {
    padding: 60px 0 0 0;
    .card-list {
      .card {
        h3 {
          font-size: 16px;
        }
      }
    }
  }
  .section-event {
    .container {
      a {
        display: inline-grid !important;
      }
      @media (max-width: 1024px) {
        padding: 0;
        .carousel {
          padding: 0;
        }
      }
    }
    .slick-list {
      padding: 0 0 0 0;
    }
    .slick-slide > div {
      display: flex;
    }
  }
  /* block-three */
  .block-three {
    background: #000000;
    padding: 60px 0 10px 0;
    margin: 40px 0px 0px;
    .br {
      border-bottom: 1px solid #ffffff;

      /* กัน CLS ช่วง slick ยังไม่ init (ก่อน init ความสูงจะน้อยกว่าหลัง init) */
      min-height: 320px;
      @media (max-width: 768px) {
        min-height: 260px;
      }
    }
    .slick-list {
      padding: 0 0 35px 0;
      min-height: 250px;
    }
    .slick-dots {
      bottom: 14px;
    }
    @media (max-width: 690px) {
      margin: 0px 0px 0px;
      padding: 30px 0;
      .title-left {
        margin-top: 0;
      }
    }
    .card {
      h3 {
        font-size: 16px;
      }
    }
    .title-left {
      a {
        color: rgb(220 21 53);
        transition: all 0.4s ease-in-out;
        &:hover {
          color: #fff;
        }
      }
    }
    .card-items {
      .card {
        &.--content {
          h3 {
            color: #fff;
          }
          p {
            color: #fff;
            @media (max-width: 768px) {
              display: none;
            }
          }
        }
      }
    }
    .variant-line.position-left .title-link {
      background-color: #000000;
      padding: 0 20px 0;
      .title {
        font-size: 45px;
      }
      @media (max-width: 1024px) {
        .title {
          font-size: 35px;
        }
      }
      @media (max-width: 768px) {
        padding: 0px;
        .title {
          font-size: 25px;
          font-weight: 400;
          letter-spacing: 2px;
        }
      }
    }

    .variant-line .full-width-line {
      background-color: #ffffff;
      top: 40%;
      @media (max-width: 768px) {
        background-color: unset;
      }
    }

    .variant-line {
      padding: 10px 0 30px;
    }

    .slick-slider {
      .slick-prev,
      .slick-next {
        top: 35%;
        &:before {
          color: #ffffff;
          font-size: 38px;
        }
      }
      .slick-prev {
        z-index: 1;
        left: 0px;
        @media (min-width: 1300px) {
          left: -40px;
        }
      }
      .slick-next {
        z-index: 1;
        right: 19px;
        @media (min-width: 1300px) {
          right: -20px;
        }
      }
    }
    .overflow-hidden {
      overflow: unset;
    }
    .slick-dots {
      display: none !important;
    }
  }
`;

export default SectionOne;
